<template>
    <li v-if="canShow" class="nav-item dropdown dropdown-notification">
        <!-- Bell toggle — native Bootstrap dropdown, like the profile menu -->
        <a ref="bellToggle" class="nav-link" id="navbarNotificationBell"
           href="#!" role="button" data-bs-toggle="dropdown" data-bs-auto-close="outside"
           aria-haspopup="true" aria-expanded="false" aria-label="Notifications">
            <span class="position-relative d-inline-flex" style="color: var(--phoenix-gray-600);">
                <vue-feather type="bell" />
                <span v-if="unreadCount > 0"
                      class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger text-white"
                      style="font-size: 0.6rem;">
                    {{ unreadCount > 99 ? '99+' : unreadCount }}
                </span>
            </span>
        </a>

        <!-- Pop-down panel -->
        <div class="dropdown-menu dropdown-menu-end navbar-dropdown-caret py-0 shadow border border-300"
             aria-labelledby="navbarNotificationBell" style="min-width: 22rem;">
            <div class="card position-relative border-0">
                <div class="card-header d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                    <span class="fw-bold">Notifications</span>
                    <button v-if="unreadCount > 0" type="button" class="btn btn-link btn-sm p-0"
                            @click="markAllRead">Mark all read</button>
                </div>
                <div class="card-body p-0" style="max-height: 24rem; overflow-y: auto;">
                    <p v-if="!panelNotifications.length" class="text-center text-700 py-4 mb-0">
                        You're all caught up.
                    </p>
                    <div v-for="n in panelNotifications" :key="n.id"
                         class="notification-card px-3 py-2 border-bottom"
                         :class="{ 'opacity-50': n.cancelled, 'notification-read': n.read && !n.cancelled }">
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="pe-2" :style="n.cancelled ? 'text-decoration: line-through;' : ''">
                                <div>
                                    <span v-if="n.cancelled" class="badge bg-secondary me-1">Retracted</span>
                                    <span v-else-if="n.level === 'urgent'" class="badge me-1"
                                          style="background-color: var(--phoenix-danger, #ed2000); color:#fff;">Urgent</span>
                                    <span class="fw-bold notification-title">{{ n.title }}</span>
                                </div>
                                <div class="text-800 fs-9 notification-message" v-html="sanitize(n.message)"></div>
                            </div>
                            <button type="button" class="btn btn-link btn-sm p-0 text-nowrap ms-2"
                                    @click="toggleRead(n)">{{ n.read ? 'Mark unread' : 'Mark read' }}</button>
                        </div>
                        <div class="text-muted mt-1" style="font-size: 0.72rem;">
                            <span v-if="n.cancelled">Retracted {{ formatTimestamp(n.cancelled_at) }}</span>
                            <span v-else-if="n.sent_at">Sent {{ formatTimestamp(n.sent_at) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </li>
</template>

<script>
import { useApiStore } from '@/store/api'
import { callApi } from '@/shared/ApiHelper'
import { sanitize as sanitizeHtml } from '@/shared/sanitize'
import dayjs from 'dayjs'
import VueFeather from 'vue-feather'

const POLL_MS = 30000

export default {
    name: 'NotificationBell',
    components: { VueFeather },

    data() {
        return { apiStore: useApiStore(), notifications: [], timer: null, autoOpenedUrgentIds: [], autoOpenedRetractedIds: [] }
    },

    computed: {
        eventId() {
            return this.apiStore.currentEvent && this.apiStore.currentEvent.event
                ? this.apiStore.currentEvent.event.id : null
        },
        attendeeId() {
            return this.apiStore.currentAttendee ? this.apiStore.currentAttendee.id : null
        },
        // Bell is visible whenever signed in (sits beside the profile icon).
        canShow() {
            return Boolean(this.attendeeId)
        },
        // Notifications are per-event, so only poll once we're in an event.
        canPoll() {
            return Boolean(this.attendeeId && this.eventId)
        },
        // The panel shows everything — read ones stay (so they can be marked unread).
        panelNotifications() {
            return this.notifications
        },
        // Unread and not retracted: drives the count badge + urgent auto-open.
        activeNotifications() {
            return this.notifications.filter((n) => !n.read && !n.cancelled)
        },
        unreadCount() {
            return this.activeNotifications.length
        },
    },

    created() {
        if (this.canPoll) {
            this.startPolling()
        }
    },

    watch: {
        canPoll(can) {
            if (can) {
                this.startPolling()
            } else {
                this.stopPolling()
                this.notifications = []
            }
        },
    },

    beforeUnmount() {
        this.stopPolling()
    },

    methods: {
        startPolling() {
            this.fetchNotifications()
            if (!this.timer) {
                this.timer = setInterval(() => this.fetchNotifications(), POLL_MS)
            }
        },

        stopPolling() {
            if (this.timer) {
                clearInterval(this.timer)
                this.timer = null
            }
        },

        // Open by triggering the toggle's own click, so it uses the same Bootstrap
        // dropdown (+ Popper) that handles manual clicks — avoids a second instance.
        openPanel() {
            this.$nextTick(() => {
                const toggle = this.$refs.bellToggle
                if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
                    toggle.click()
                }
            })
        },

        sanitize(html) {
            return sanitizeHtml(html || '')
        },

        formatTimestamp(iso) {
            return iso ? dayjs(iso).format('MMM D, YYYY h:mm A') : ''
        },

        async fetchNotifications() {
            if (!this.canPoll) return
            // The server resolves the attendee from the authenticated request;
            // it does not trust a client-supplied attendee id.
            const url = this.apiStore.buildUrl(
                `/events/notifications/?event=${this.eventId}`
            )
            const response = await callApi(url)
            if (response && Array.isArray(response.data)) {
                this.notifications = response.data
                this.maybeAutoOpenForUrgent()
            }
        },

        // Pop the panel open when a NEW unread urgent notification appears (a send) OR
        // when an urgent notification is newly retracted. Retractions of urgent notices
        // get the SAME attention as urgent sends — same auto-open logic, tracked separately
        // (a retracted notification is no longer in activeNotifications).
        maybeAutoOpenForUrgent() {
            const urgentSendIds = this.activeNotifications
                .filter((n) => n.level === 'urgent')
                .map((n) => n.id)
            const urgentRetractIds = this.notifications
                .filter((n) => n.level === 'urgent' && n.cancelled)
                .map((n) => n.id)
            // Accumulate the ids we've already auto-opened for (never replace), so
            // toggling a notification read -> unread does NOT re-pop the panel — only
            // a genuinely new urgent id (or a newly-retracted urgent) opens it.
            const newSendIds = urgentSendIds.filter((id) => !this.autoOpenedUrgentIds.includes(id))
            const newRetractIds = urgentRetractIds.filter((id) => !this.autoOpenedRetractedIds.includes(id))
            if (newSendIds.length || newRetractIds.length) {
                this.autoOpenedUrgentIds = [...this.autoOpenedUrgentIds, ...newSendIds]
                this.autoOpenedRetractedIds = [...this.autoOpenedRetractedIds, ...newRetractIds]
                this.openPanel()
            }
        },

        async toggleRead(notification) {
            const action = notification.read ? 'mark_unread' : 'mark_read'
            const url = this.apiStore.buildUrl(`/events/notifications/${notification.id}/${action}/`)
            const response = await callApi(url, 'POST')
            if (response && response.status >= 200 && response.status <= 202) {
                notification.read = !notification.read
            }
        },

        async markAllRead() {
            for (const n of this.notifications.filter((x) => !x.read)) {
                const url = this.apiStore.buildUrl(`/events/notifications/${n.id}/mark_read/`)
                const response = await callApi(url, 'POST')
                if (response && response.status >= 200 && response.status <= 202) {
                    n.read = true
                }
            }
        },
    },
}
</script>

<style scoped>
/* Read notifications are visually muted (beyond the text change): dimmed + gray
   background, so unread clearly stands out. Cancelled keeps its own opacity-50 +
   strike-through and takes precedence (the read class is guarded by !n.cancelled). */
/* Title reads as a bold heading: force 700, darkest gray, slightly larger than the
   message. Scoped + explicit so theme/body-font defaults can't soften it. */
.notification-title {
    font-weight: 700;
    font-size: 0.9rem;
    color: var(--phoenix-gray-1000, #141414);
}
.notification-read {
    background-color: var(--phoenix-gray-100, #f5f5f5);
    opacity: 0.6;
}
.notification-read .notification-title {
    color: var(--phoenix-gray-700, #6c757d);
}
</style>
